import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { resolveTenant } from "../tenants";
import { fetchSystemSettings } from "../services/api";
import { formatCurrency as fmtCurrency, currencySymbol as getCurrencySymbol } from "../utils/currency";

// Static fallback for initial render before API config arrives.
// Deprecated: prefer passing config from TenantBootstrap.
const staticTenant = resolveTenant();

const TenantContext = createContext({
  ...staticTenant,
  currency: staticTenant.currency,
  formatCurrency: (amount) => fmtCurrency(amount, staticTenant.currency),
  currencySymbol: () => getCurrencySymbol(staticTenant.currency),
  reloadSettings: () => {},
});

/**
 * TenantProvider — accepts an optional `config` prop from TenantBootstrap
 * (API-driven tenant config). Falls back to static resolveTenant() if no
 * config prop is provided (backwards compat).
 */
export function TenantProvider({ config, children }) {
  const tenant = config || staticTenant;
  const [currency, setCurrency] = useState(tenant.currency);

  // Update currency when config changes (e.g. API config arrives after cached render)
  useEffect(() => {
    if (tenant.currency) setCurrency(tenant.currency);
  }, [tenant.currency]);

  const loadSettings = useCallback(() => {
    fetchSystemSettings()
      .then((settings) => {
        if (settings?.currency) setCurrency(settings.currency);
      })
      .catch(() => {/* use tenant default */});
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const value = {
    ...tenant,
    currency,
    formatCurrency: (amount) => fmtCurrency(amount, currency),
    currencySymbol: () => getCurrencySymbol(currency),
    reloadSettings: loadSettings,
  };

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext);
}
